import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart3,
  TrendingUp,
  MousePointerClick,
  Eye,
  DollarSign,
  Target,
  ArrowUpRight,
  Activity
} from 'lucide-react';

const AnalyticsDashboard = ({ embedded = false }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const data = await api.getMyAnalytics();
        setAnalytics(data);
      } catch (error) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return embedded ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-muted-foreground">Loading analytics...</div>
        </div>
    ) : (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-muted-foreground">Loading analytics...</div>
        </div>
      </Layout>
    );
  }

  const sigStats = analytics?.signature_stats || {};
  const adStats = analytics?.my_ads_stats || {};
  const dailyChart = analytics?.daily_chart || [];
  const topPlacements = analytics?.top_placements || [];

  const maxImpressions = Math.max(...dailyChart.map(d => d.impressions), 1);

  const content = (
      <div className="space-y-8" data-testid="analytics-dashboard">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Performance Analytics</h1>
          <p className="text-muted-foreground mt-1">Track your signature and ad performance</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <Eye className="w-5 h-5 text-blue-500" />
                <Badge variant="secondary" className="text-xs">Signatures</Badge>
              </div>
              <p className="text-2xl font-bold" data-testid="metric-impressions">{sigStats.total_impressions || 0}</p>
              <p className="text-sm text-muted-foreground">Total Impressions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <MousePointerClick className="w-5 h-5 text-green-500" />
                <Badge variant="secondary" className="text-xs">Signatures</Badge>
              </div>
              <p className="text-2xl font-bold" data-testid="metric-clicks">{sigStats.total_clicks || 0}</p>
              <p className="text-sm text-muted-foreground">Total Clicks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <Target className="w-5 h-5 text-amber-500" />
                <Badge variant="secondary" className="text-xs">CTR</Badge>
              </div>
              <p className="text-2xl font-bold" data-testid="metric-ctr">{sigStats.ctr || 0}%</p>
              <p className="text-sm text-muted-foreground">Click-Through Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <DollarSign className="w-5 h-5 text-green-600" />
                <Badge variant="secondary" className="text-xs">Revenue</Badge>
              </div>
              <p className="text-2xl font-bold text-green-600" data-testid="metric-earnings">
                ${sigStats.total_earnings?.toFixed(2) || '0.00'}
              </p>
              <p className="text-sm text-muted-foreground">Total Earnings</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Daily Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Daily Activity (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dailyChart.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No activity data yet</p>
                  <p className="text-sm mt-1">Data will appear as your signature ads get views and clicks</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {dailyChart.slice(-14).map((day) => (
                    <div key={day.date} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-20 shrink-0">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-5 overflow-hidden">
                          <div
                            className="h-full bg-blue-500/70 rounded-full flex items-center justify-end pr-2"
                            style={{ width: `${Math.max((day.impressions / maxImpressions) * 100, 5)}%` }}
                          >
                            <span className="text-[10px] text-white font-medium">{day.impressions}</span>
                          </div>
                        </div>
                        <div className="w-14 text-right">
                          <span className="text-xs font-medium text-green-600">{day.clicks} clicks</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center gap-4 pt-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-blue-500/70 rounded" />
                      <span>Impressions</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-green-600 font-medium">Clicks</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Ads Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                My Ads Performance
              </CardTitle>
              <CardDescription>Ads you've placed on other signatures</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{adStats.active_ads || 0}</p>
                  <p className="text-xs text-muted-foreground">Active Ads</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{adStats.total_impressions || 0}</p>
                  <p className="text-xs text-muted-foreground">Impressions</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{adStats.ctr || 0}%</p>
                  <p className="text-xs text-muted-foreground">CTR</p>
                </div>
              </div>
              {adStats.active_ads === 0 && (
                <div className="text-center py-6 text-muted-foreground">
                  <p className="text-sm">No active ads yet. Place ads in the Marketplace to get started!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Top Performing Placements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5" />
              Top Performing Ad Placements
            </CardTitle>
            <CardDescription>Highest click-through ads on your signatures</CardDescription>
          </CardHeader>
          <CardContent>
            {topPlacements.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No ad placements yet</p>
                <p className="text-sm mt-1">Once advertisers place ads on your signature, their performance data will show here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topPlacements.map((p, i) => (
                  <div key={p.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-bold text-muted-foreground">#{i + 1}</span>
                      <div>
                        <p className="font-medium">{p.advertiser_name || 'Unknown'}</p>
                        <p className="text-sm text-muted-foreground">{p.advertiser_company}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="font-medium">{p.impressions || 0}</p>
                        <p className="text-xs text-muted-foreground">Views</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-green-600">{p.clicks || 0}</p>
                        <p className="text-xs text-muted-foreground">Clicks</p>
                      </div>
                      <div className="text-center">
                        <p className="font-medium text-amber-600">
                          {p.impressions > 0 ? ((p.clicks || 0) / p.impressions * 100).toFixed(1) : 0}%
                        </p>
                        <p className="text-xs text-muted-foreground">CTR</p>
                      </div>
                      <Badge variant="outline" className="text-green-600">${(p.amount_paid * 0.5).toFixed(2)} earned</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

export default AnalyticsDashboard;
